import React, { useEffect, useMemo } from "react";
import { useDesign } from "../../context/contextDesign";
import { useNav } from "../../context/contextNav";
import { useThemeMode } from "../../context/contextTheme";
import { useWeb } from "../../context/Dashboard/contextWeb";
import { useNetwork } from "../../context/Dashboard/contextNetwork";
import reportItems from "../../config/reportItems";
import hexToRGBA from "../../utils/hexToRGBA";

const CLOUD_TABS = ["AWS Reports", "Azure Reports", "GCP Reports", "OCI Reports"];

const Reports = () => {
  const { theme } = useThemeMode();
  const { zoom, bHeightVal } = useDesign();
  const {
    reportTab,
    setReportTab,
    reportSubTab,
    setReportSubTab,
  } = useNav();  
  const { webResults, setUrlVulTable, selectedScanIndex: sw } = useWeb();
  const { networkResults, setIpVulTable, selectedScanIndex: sn } = useNetwork();

  // --- Core Logic ---
  useEffect(() => {
    if (!reportTab || !webResults?.length) return;
    const scanWeb = webResults[sw < webResults.length ? sw : 0]?.vuln_data;
    if (scanWeb) setUrlVulTable(scanWeb);
  }, [reportTab, webResults, sw, setUrlVulTable]);

  useEffect(() => {
    if (!reportTab || !networkResults?.length) return;
    const scanNet = networkResults[sn < networkResults.length ? sn : 0]?.vuln_data;
    if (scanNet) setIpVulTable(scanNet);
  }, [reportTab, networkResults, sn, setIpVulTable]);

  const isCloud = CLOUD_TABS.includes(reportTab);
  const mainTabs = useMemo(() => [...new Set(reportItems.map(item => item.tab))], []);
  
  const cloudSubTabs = useMemo(() => {
    if (!isCloud) return [];
    return reportItems.find(i => i.tab === reportTab)?.submenu || [];
  }, [isCloud, reportTab]);

  useEffect(() => {
    if (!isCloud || !cloudSubTabs.length) return;
    if (!cloudSubTabs.some((item) => item.subTab === reportSubTab)) {
      setReportSubTab(cloudSubTabs[0].subTab);
    }
  }, [cloudSubTabs, isCloud, reportSubTab, setReportSubTab]);

  const selectedReport = useMemo(() => {
    if (isCloud) return cloudSubTabs.find(i => i.subTab === reportSubTab) || null;
    return reportItems.find(item => item.tab === reportTab && !item.submenu) || null;
  }, [isCloud, reportTab, reportSubTab, cloudSubTabs]);

  // --- Styled Components (Native Look) ---
  const navItemStyle = (isActive) => ({
    padding: "12px 16px",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "0.2s",
    fontSize: "0.85rem",
    fontWeight: isActive ? "700" : "500",
    backgroundColor: isActive ? "#344266" : "transparent",
    color: isActive ? "#fff" : theme.textMuted,
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "4px"
  });

  return (
    <div 
      className="d-flex p-3 gap-3 overflow-hidden" 
      style={{ height: bHeightVal, zoom, backgroundColor: hexToRGBA(theme.background, 0.4) }}
    >
      {/* 1. SIDEBAR: Fixed width, height fills parent */}
      <div 
        className="card border-0 rounded-4 shadow-sm flex-shrink-0 d-flex flex-column p-3 h-100" 
        style={{ width: "280px", background: theme.background }}
      >
        <div className="mb-4 px-2 small fw-bold text-uppercase opacity-50" style={{ letterSpacing: "1px" }}>
          Navigation
        </div>
        <div className="flex-grow-1 overflow-y-auto custom-scrollbar">
          {mainTabs.map(tab => (
            <div 
              key={tab} 
              style={navItemStyle(reportTab === tab)} 
              onClick={() => { 
                setReportTab(tab); 
                setReportSubTab(null); 
              }}
            >
              <i className={`bi bi-${tab.includes('Cloud') ? 'cloud-fill' : 'file-bar-graph'}`} />
              {tab}
            </div>
          ))}
        </div>
      </div>

      {/* 2. MAIN CONTENT: Fills remaining horizontal space */}
      <div className="flex-grow-1 d-flex flex-column overflow-hidden">
        <div 
          className="card border-2 rounded-4 shadow-sm h-100 d-flex flex-column overflow-hidden" 
          style={{ background: theme.background }}
        >
          {/* Content Header (Sticky at top) */}
          <div className="p-4 border-bottom flex-shrink-0">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="fw-bold mb-0" style={{ color: theme.textColor }}>{reportTab}</h4>
                <div className="text-muted small">Systems analysis and vulnerability report</div>
              </div>

              {isCloud && (
                <div className="d-flex bg-light p-1 rounded-3">
                  {cloudSubTabs.map(sub => (
                    <button
                      key={sub.key}
                      className="btn btn-sm border-0 px-3 fw-bold"
                      style={{
                        fontSize: "0.7rem",
                        backgroundColor: reportSubTab === sub.subTab ? "#fff" : "transparent",
                        color: reportSubTab === sub.subTab ? "#344266" : theme.textMuted,
                        boxShadow: reportSubTab === sub.subTab ? "0 2px 5px rgba(0,0,0,0.1)" : "none"
                      }}
                      onClick={() => setReportSubTab(sub.subTab)}
                    >
                      {sub.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Content Body (Internal Scrolling) */}
          <div className="flex-grow-1 overflow-y-auto p-4 custom-scrollbar">
            {selectedReport ? (
              <selectedReport.component selectTab={reportTab} />
            ) : (
              <div className="h-100 d-flex flex-column align-items-center justify-content-center opacity-50">
                <i className="bi bi-search mb-2 fs-1" />
                <span>Select a category to view data</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
